import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { format, parseISO } from 'date-fns';

import icClaimNote from 'core/assets/svg/ic-claim-note.svg';
import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import { StatusPill } from 'core/components/status-pill';
import { LAYMAN_DATE_FORMAT, shortLaymanDateTimeFormatter } from 'core/helpers/formatters';
import { GET_POLICY_PREVIEW_DRAFT_NOTES } from '../policy.queries';
import useStyles from './policy-draft-notes-modal.styles';

const PolicyDraftNotesModal = ({ onClose, policyId, previewId, effectiveDate }) => {
  const classes = useStyles();
  const { data, loading, error } = useQuery(GET_POLICY_PREVIEW_DRAFT_NOTES, {
    variables: { policyId, previewId },
    fetchPolicy: 'no-cache'
  });
  const notes = data?.getPolicyPreviewDraftNotes ?? [];

  return (
    <BaseDialog size="sm" open onClose={onClose} className={classes.dialog}>
      <Label type="h1">Notes</Label>
      <Label type="subtitle" className={classes.subtitle}>
        {effectiveDate ? `${format(parseISO(effectiveDate), LAYMAN_DATE_FORMAT)}, draft` : 'Draft'}
      </Label>
      <hr className={classes.divider} />
      {loading && <Loading noBackground />}
      {error && (
        <Label type="body2" className={classes.error}>
          The notes couldn&apos;t be loaded. Close this and try again.
        </Label>
      )}
      {notes.map((note) => (
        <div key={`${note.previewId}-${note.createdAt}`} className={classes.note}>
          <div className={classes.noteHeader}>
            <img src={icClaimNote} alt="" className={classes.noteIcon} />
            <StatusPill status={note.status} />
            <Label type="body2" className={classes.noteAuthor}>
              {note.createdBy} on {shortLaymanDateTimeFormatter(note.createdAt)}
            </Label>
          </div>
          {[note.internalNote, note.note].filter(Boolean).map((text) => (
            <Label key={text} type="body1" className={classes.noteText}>
              {text}
            </Label>
          ))}
        </div>
      ))}
      <div className={classes.actions}>
        <Button variant="contained" color="primary" onClick={onClose} className={classes.closeButton}>
          Close
        </Button>
      </div>
    </BaseDialog>
  );
};

PolicyDraftNotesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  policyId: PropTypes.string.isRequired,
  previewId: PropTypes.string.isRequired,
  effectiveDate: PropTypes.string
};

PolicyDraftNotesModal.defaultProps = {
  effectiveDate: null
};

export default PolicyDraftNotesModal;
